import { Grid } from "@freightsimple/ui-core/PageLayout/Grid";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { type PerformanceFreightProfile } from "@freightsimple/generated-carriers-openapi-client/models/PerformanceFreightProfile";
import { PerformanceBreakdownTable } from "@/App/Performance/PerformanceBreakdownTable";

/**
 * The shape of the freight this carrier hauls for us — aimed at their pricing team.
 *
 * Distributions rather than averages throughout: a mean weight sits somewhere no
 * shipment really is as soon as there's one heavy outlier, and a pricing team
 * can't price an average. Density leads because density drives the class, and the
 * class drives the rate.
 */
export function FreightProfileGrid({
  freightProfile,
}: {
  freightProfile: PerformanceFreightProfile;
}) {
  return (
    <Stack gap={4}>
      <T size="sm" color="secondary">
        Based on the {freightProfile.shipments.toLocaleString("en-CA")}{" "}
        shipments you carried for us in this period.
      </T>
      <Grid columns={2} gap="lg">
        <PerformanceBreakdownTable
          title="Density"
          description="Density drives the freight class, and the class drives the rate."
          header="Density"
          rows={freightProfile.densityBands}
        />
        <PerformanceBreakdownTable
          title="Weight"
          description="How the shipments distribute by weight."
          header="Weight"
          rows={freightProfile.weightBands}
        />
        <PerformanceBreakdownTable
          title="Linear Feet"
          description="Trailer space per shipment."
          header="Linear Feet"
          rows={freightProfile.linearFeetBands}
        />
        <PerformanceBreakdownTable
          title="Handling Units"
          description="Skids and pieces per shipment."
          header="Handling Units"
          rows={freightProfile.handlingUnitBands}
        />
        <PerformanceBreakdownTable
          title="Where It Delivers"
          description="Delivery provinces and states, busiest first."
          header="Region"
          rows={freightProfile.deliveryRegions}
        />
        <PerformanceBreakdownTable
          title="Service Mix"
          description="Which of your services the freight moves on."
          header="Service"
          rows={freightProfile.serviceMix}
        />
      </Grid>
    </Stack>
  );
}
